/**
 * Phase 2C-ii.4 · pinned pre-pour compliance banner.
 *
 * Surfaces the latest predicted SolverResult vs governing-spec verdict
 * across the project workspace shell. Read-only; sits above the per-tab
 * Outlet so the signal is visible from any tab.
 *
 * - Pro tier only (backend gate is the source of truth, but we also
 *   skip the fetch entirely for non-Pro to avoid a guaranteed 403).
 * - `no_data` → renders nothing (no clutter on empty projects).
 * - `within_spec` → thin teal-edged band, mono pass summary.
 * - `exceeding_spec` → thin amber-edged band, first failing check.
 * - Dismissable per-session via sessionStorage key per project.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";

import { authService } from "@/services/authService";
import { useTier } from "@/contexts/AuthContext";

const DISMISS_KEY = (pid) => `concretecontrol.compliance.dismissed.${pid}`;

export const ComplianceBanner = ({ projectId }) => {
    const { meets } = useTier();
    const isPro = meets("professional");

    const [verdict, setVerdict] = useState(null);
    const [dismissed, setDismissed] = useState(() => {
        try { return sessionStorage.getItem(DISMISS_KEY(projectId)) === "1"; }
        catch { return false; }
    });

    const fetchVerdict = useCallback(async () => {
        if (!isPro || !projectId) return;
        try {
            const res = await authService.http.get(`/projects/${projectId}/compliance_verdict`);
            setVerdict(res.data);
        } catch {
            setVerdict(null);
        }
    }, [isPro, projectId]);

    useEffect(() => { fetchVerdict(); }, [fetchVerdict]);

    // Refresh when the tab regains focus (state-flip signal, not live counter).
    useEffect(() => {
        const onFocus = () => fetchVerdict();
        window.addEventListener("focus", onFocus);
        return () => window.removeEventListener("focus", onFocus);
    }, [fetchVerdict]);

    const failingCheck = useMemo(() => {
        if (!verdict || verdict.verdict !== "exceeding_spec") return null;
        return (verdict.checks || []).find((c) => !c.pass) || null;
    }, [verdict]);

    if (!isPro || !verdict) return null;
    if (verdict.verdict === "no_data") return null;
    if (dismissed) return null;

    const onDismiss = () => {
        try { sessionStorage.setItem(DISMISS_KEY(projectId), "1"); } catch {}
        setDismissed(true);
    };

    const within = verdict.verdict === "within_spec";
    const tone = within
        ? { border: "border-[#01696f]", edge: "bg-[#01696f]", text: "text-[#01696f]" }
        : { border: "border-[#b3741f]", edge: "bg-[#b3741f]", text: "text-[#b3741f]" };

    const passCount = (verdict.checks || []).filter((c) => c.pass).length;
    const totalCount = (verdict.checks || []).length;

    return (
        <div
            data-testid={`compliance-banner-${verdict.verdict}`}
            className={`mb-5 border ${tone.border} bg-[#ffffff] flex items-stretch gap-0 relative`}
        >
            <span className={`w-[3px] ${tone.edge}`} aria-hidden="true" />
            <div className="flex-1 flex flex-wrap items-center gap-3 px-4 py-3">
                <span
                    data-testid="compliance-banner-eyebrow"
                    className={`tc-mono text-[10px] tracking-[0.18em] uppercase ${tone.text}`}
                >
                    {within ? "within spec" : "exceeds spec"}
                </span>
                <span
                    data-testid="compliance-banner-summary"
                    className="tc-mono text-[11.5px] text-[#0e1416] leading-[1.5] max-w-[78ch]"
                >
                    {within ? (
                        <>
                            Pre-pour design within{" "}
                            <span className="text-[#0e1416]">{verdict.spec_preset_code || "spec"}</span>
                            {" · "}
                            {passCount}/{totalCount} checks pass · solver: {verdict.solver_label || "—"}
                        </>
                    ) : (
                        <>
                            Pre-pour design exceeds{" "}
                            <span className="text-[#0e1416]">{verdict.spec_preset_code || "spec"}</span>
                            {failingCheck && (
                                <>
                                    {" · "}
                                    <span data-testid="compliance-banner-failing-check">
                                        {failingCheck.name} predicted {failingCheck.predicted}
                                        {failingCheck.unit ? ` ${failingCheck.unit}` : ""} vs limit{" "}
                                        {failingCheck.limit}
                                        {failingCheck.unit ? ` ${failingCheck.unit}` : ""}
                                    </span>
                                </>
                            )}
                            {" · solver: "}{verdict.solver_label || "—"}
                        </>
                    )}
                </span>
                <Link
                    to={`/app/projects/${projectId}/reports`}
                    data-testid="compliance-banner-view-report"
                    className={`tc-mono text-[11px] ml-auto ${tone.text} hover:underline`}
                >
                    View report →
                </Link>
                <button
                    type="button"
                    onClick={onDismiss}
                    data-testid="compliance-banner-dismiss"
                    aria-label="Dismiss compliance banner"
                    className="text-[#5a6669] hover:text-[#0e1416] -mr-1 p-1"
                >
                    <X size={13} strokeWidth={1.5} />
                </button>
            </div>
        </div>
    );
};

export default ComplianceBanner;
